import { useTranslation } from "react-i18next";
import type { SchemaElement } from "../../../api/restaurantSchema";
import type { TableStatus } from "../../../components/restaurant-schema/tableTones";
import type { FloorPicker } from "../components/FloorCanvas";
import { canTogglePick, pickStateOf, type PickContext } from "../logic/tablePick";
import type { NewReservationForm } from "./useNewReservationForm";
import { useReservationFormat } from "./useReservationFormat";

/** Контекстът на избора от състоянието на формата. */
export const pickContextOf = (form: NewReservationForm): PickContext => ({
  selectedIds: form.draft.tableIds,
  busyTableIds: form.busyTableIds,
  reclaimableTableIds: form.reclaimableTableIds,
  isDayUnknown: form.isDayUnknown,
  guests: form.draft.guests,
});

/**
 * Режимът „избор на маса" върху платното, докато е отворена „Нова
 * резервация" (null иначе): избрана = оранжев контур + „✓ Избрана"; заета в
 * интервала = щриховано „Заета", некликаема; побира гостите = зелен контур;
 * останалите — обикновени. Клик избира/маха масата; табовете на регионите
 * броят незаетите в интервала.
 */
export const useTablePicker = ({
  form,
  open,
}: {
  form: NewReservationForm;
  open: boolean;
}): FloorPicker | null => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  if (!open) return null;

  const { draft, busyTableIds, reclaimableTableIds } = form;
  const isSelected = (table: SchemaElement) => draft.tableIds.includes(table.id);
  // Маса на редактираната резервация при непроменен час не е „заета" за нея.
  const isReclaimable = (table: SchemaElement) => reclaimableTableIds.has(table.id);
  const isBusy = (table: SchemaElement) => busyTableIds.has(table.id) && !isReclaimable(table);
  const fitsGuests = (table: SchemaElement) => (table.seats ?? 0) >= draft.guests;

  const context = pickContextOf(form);
  const stateOf = (table: SchemaElement) => pickStateOf({ id: table.id, seats: table.seats ?? 0 }, context);

  // На малка маса надписът не се събира — остава само ✓ / щриховката (compactText "").
  const tableStatus = (table: SchemaElement): TableStatus => {
    switch (stateOf(table)) {
      case "selected":
        return { tone: "selected", badge: { text: t("reservations.picker.badgeSelected"), compactText: "", icon: "check" } };
      // Заетостта на деня още се зарежда — масите не се избират, докато не е ясна.
      case "unknown":
        return { tone: "free", disabled: true };
      case "busy":
        return { tone: "blocked", badge: { text: t("reservations.picker.badgeBusy"), compactText: "" }, disabled: true };
      case "fits":
        return { tone: "fits" };
      case "free":
        return { tone: "free" };
    }
  };

  const tableTitle = (table: SchemaElement) => {
    const base = t("reservations.table.hover", {
      name: table.name ?? "",
      seats: format.seats(table.seats ?? 0),
    });
    const detail = isSelected(table)
      ? t("reservations.picker.hoverSelected")
      : isBusy(table)
        ? t("reservations.picker.hoverBusy")
        : fitsGuests(table)
          ? t("reservations.picker.hoverFits")
          : null;
    return detail ? `${base} · ${detail}` : base;
  };

  return {
    tableStatus,
    tableTitle,
    onTableClick: (table) => {
      // Докато заетостта не е известна — само махане на избрана маса.
      if (canTogglePick(stateOf(table))) form.toggleTable(table.id);
    },
  };
};
